import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SectionCard from "features/TraxxionProfile/components/SectionCard";
import SectionRow from "features/TraxxionProfile/components/SectionRow";
import { cn } from "../../../../lib/utils";

function Settings({ user }: any) {
    const { t } = useTranslation();

    const breaks = useMemo(() => {
        const { automatic, manual } = user?.calculationGroupRules?.breaks || {};
        if (!automatic && !manual) return { label: t("no-break-settings"), hasRules: false };

        let label: string;
        if (!automatic?.status && !manual?.status) {
            label = t("breaks-disabled");
        } else if (automatic?.status) {
            label = t("automatic-breaks");
        } else {
            label = t("manual-breaks");
        }
        return { label, hasRules: true };
    }, [t, user?.calculationGroupRules?.breaks]);

    const overtime = useMemo(() => {
        const ot = user?.calculationGroupRules?.overtime;
        if (!ot || !Object.keys(ot).length) return { rules: [], hasRules: false };
        return {
            rules: Object.keys(ot).map((key) => ({ label: t(`${key}-rule`), status: ot[key].status })),
            hasRules: true,
        };
    }, [t, user?.calculationGroupRules?.overtime]);

    const enabledCount = overtime.rules.filter((r) => r.status).length;

    const twoFAStatus = user?.twoFA?.status;
    const twoFAType = user?.twoFA?.type;

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-border tw-rounded-xl tw-p-5">
                <h6 className="tw-font-semibold tw-mb-3">{t("calculation-group-rules")}</h6>

                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-muted-foreground tw-mb-2 tw-border-b tw-py-2">
                    <h4>{t("breaks")}</h4>
                    <span className={cn(breaks.hasRules ? "tw-text-neutral-900" : "tw-italic")}>
                        {breaks.label}
                    </span>
                </div>

                <div className="tw-border-b tw-py-2 last:tw-border-b-0 last:tw-mb-0 tw-mb-2 tw-text-sm tw-text-muted-foreground">
                    <div className="tw-flex tw-items-center tw-justify-between">
                        <h4>{t("overtime")}</h4>
                        {overtime.hasRules ? (
                            <span className="tw-text-xs tw-text-muted-foreground">
                                {enabledCount} / {overtime.rules.length} {t("enabled")}
                            </span>
                        ) : (
                            <span className="tw-italic">{t("no-overtime-settings")}</span>
                        )}
                    </div>
                    {overtime.hasRules && (
                        <ul className="tw-mt-2 tw-space-y-1">
                            {overtime.rules.map((rule, i) => (
                                <li key={i} className="tw-flex tw-items-center">
                                    <span className="tw-flex-1 tw-text-neutral-900">{rule.label}</span>
                                    <span className={cn(
                                        "tw-w-16 tw-text-xs tw-text-center",
                                        rule.status ? "tw-text-primary tw-font-medium" : "tw-italic"
                                    )}>
                                        {rule.status ? t("enabled") : t("disabled")}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <SectionCard title={t("two-factor-auth")}>
                <SectionRow label={t("status")} value={twoFAStatus ? t(twoFAStatus) : undefined} />
                <SectionRow label={t("type")} value={twoFAType ? t(twoFAType) : undefined} />
                <SectionRow label={t("email")} value={user?.twoFA?.email ?? undefined} />
            </SectionCard>
        </div>
    );
}

export default Settings;
